/*
 * Webmail's queries, and the actions on messages. Counts always come from
 * the server (IMAP STATUS); after any change the affected lists are fetched
 * again rather than guessed.
 */
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../ui/Toast";
import { ApiError, qs, wmApi } from "./api";
import type { ContactsPage, Conversation, FoldersResult, HitsResult, ListResult, Message, Row, ScheduledMessage, Settings, SnoozedMessage, UnifiedResult } from "./types";

export const STARRED = "~starred";
/** Every mailbox signed in to in this session, their Inboxes merged. */
export const UNIFIED = "~all";

export type ListView = { kind: "folder"; folder: string; page: number } | { kind: "starred" } | { kind: "search"; q: string } | { kind: "unified"; page: number };

export const keys = {
  all: (mb: string) => ["wm", mb] as const,
  folders: (mb: string) => ["wm", mb, "folders"] as const,
  lists: (mb: string) => ["wm", mb, "list"] as const,
  list: (mb: string, v: ListView) => ["wm", mb, "list", v] as const,
  conversations: (mb: string) => ["wm", mb, "conv"] as const,
  conversation: (mb: string, folder: string, uid: number, images: boolean) => ["wm", mb, "conv", folder, uid, images] as const,
  settings: (mb: string) => ["wm", mb, "settings"] as const,
  scheduled: (mb: string) => ["wm", mb, "scheduled"] as const,
  snoozed: (mb: string) => ["wm", mb, "snoozed"] as const,
  contacts: (mb: string) => ["wm", mb, "contacts"] as const,
  /** The session's, not one mailbox's. */
  unified: ["wm", "unified"] as const,
};

export function useFolders(mb: string) {
  return useQuery({ queryKey: keys.folders(mb), queryFn: () => wmApi.get<FoldersResult>(`/m/${mb}/folders`), refetchInterval: 120_000 });
}

export function useList(mb: string, v: ListView) {
  return useQuery({
    // The unified inbox is the session's: the same list whichever mailbox is open.
    queryKey: v.kind === "unified" ? [...keys.unified, v.page] : keys.list(mb, v),
    queryFn: async (): Promise<ListResult | HitsResult | UnifiedResult> => {
      if (v.kind === "unified") return wmApi.get<UnifiedResult>(`/unified${qs({ page: v.page > 1 ? v.page : null })}`);
      if (v.kind === "starred") return wmApi.get<HitsResult>(`/m/${mb}/starred`);
      if (v.kind === "search") return wmApi.get<HitsResult>(`/m/${mb}/search${qs({ q: v.q })}`);
      return wmApi.get<ListResult>(`/m/${mb}/messages${qs({ folder: v.folder, page: v.page })}`);
    },
    // Keep the page on screen while the next page of the same folder loads; never another folder's rows.
    placeholderData: (prev, prevQuery) => {
      const pv = prevQuery?.queryKey[3] as ListView | undefined;
      return pv && pv.kind === "folder" && v.kind === "folder" && pv.folder === v.folder ? prev : undefined;
    },
  });
}

/** The clicked message on its own: what the reading pane shows first. */
export function useMessage(mb: string, open: { folder: string; uid: number } | null, images: boolean) {
  return useQuery({
    queryKey: open ? (["wm", mb, "conv", open.folder, open.uid, images, "one"] as const) : ["wm", mb, "conv", "none", "one"],
    queryFn: async () => (await wmApi.get<{ message: Message }>(`/m/${mb}/message${qs({ folder: open!.folder, uid: open!.uid, images })}`)).message,
    enabled: !!open,
    staleTime: 60_000,
  });
}

/** The whole conversation, asked for once the clicked message is on screen (they share one IMAP connection). */
export function useConversation(mb: string, open: { folder: string; uid: number } | null, images: boolean, enabled: boolean) {
  return useQuery({
    queryKey: open ? keys.conversation(mb, open.folder, open.uid, images) : ["wm", mb, "conv", "none"],
    queryFn: () => wmApi.get<Conversation>(`/m/${mb}/conversation${qs({ folder: open!.folder, uid: open!.uid, images })}`),
    enabled: !!open && enabled,
    staleTime: 60_000,
  });
}

/** Scheduled sends: when each goes, and why one was postponed or failed. */
export function useScheduled(mb: string, enabled = true) {
  return useQuery({ queryKey: keys.scheduled(mb), queryFn: async () => (await wmApi.get<{ scheduled: ScheduledMessage[] }>(`/m/${mb}/scheduled`)).scheduled, enabled, refetchInterval: 60_000 });
}

/** Snoozed mail: when each comes back. */
export function useSnoozed(mb: string, enabled = true) {
  return useQuery({ queryKey: keys.snoozed(mb), queryFn: async () => (await wmApi.get<{ snoozed: SnoozedMessage[] }>(`/m/${mb}/snoozed`)).snoozed, enabled, refetchInterval: 60_000 });
}

export function useContacts(mb: string, q: string, page: number) {
  return useQuery({
    queryKey: [...keys.contacts(mb), q, page],
    queryFn: () => wmApi.get<ContactsPage>(`/m/${mb}/contacts${qs({ q: q || null, page: page > 1 ? page : null })}`),
    placeholderData: (prev) => prev,
  });
}

export function useSettings(mb: string) {
  return useQuery({ queryKey: keys.settings(mb), queryFn: async () => (await wmApi.get<{ settings: Settings }>(`/m/${mb}/settings`)).settings, staleTime: 60_000 });
}

/** Groups rows by folder: one request per folder, one IMAP command per batch. */
export function byFolder(rows: Row[]): Map<string, number[]> {
  const m = new Map<string, number[]>();
  for (const r of rows) m.set(r.folder, [...(m.get(r.folder) ?? []), ...r.uids]);
  return m;
}

/** Groups rows by mailbox and folder (the unified inbox mixes mailboxes): each group uses its own mailbox's routes. */
export function byMailboxFolder(rows: Row[], mb: string): { mailbox: string; folder: string; uids: number[] }[] {
  const m = new Map<string, { mailbox: string; folder: string; uids: number[] }>();
  for (const r of rows) {
    const mailbox = r.mailboxId ?? mb;
    const k = `${mailbox}\u0000${r.folder}`;
    const g = m.get(k) ?? { mailbox, folder: r.folder, uids: [] };
    g.uids.push(...r.uids);
    m.set(k, g);
  }
  return [...m.values()];
}

/** "Tue, Oct 22 at 8:00 AM" in the browser's time zone; "today" and "tomorrow" said as such. */
export function whenWords(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(d) - day(now)) / 86_400_000);
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (diff === 0) return `today at ${time}`;
  if (diff === 1) return `tomorrow at ${time}`;
  return `${d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) })} at ${time}`;
}

export const describe = (n: number, what = "conversation") => `${n.toLocaleString("en-US")} ${what}${n === 1 ? "" : "s"}`;

export function useMailActions(mb: string) {
  const qc = useQueryClient();
  const toast = useToast();

  const refresh = useCallback(async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: keys.folders(mb) }),
      qc.invalidateQueries({ queryKey: keys.lists(mb) }),
      qc.invalidateQueries({ queryKey: keys.conversations(mb) }),
      qc.invalidateQueries({ queryKey: keys.unified }),
      qc.invalidateQueries({ queryKey: keys.snoozed(mb) }),
    ]);
  }, [qc, mb]);

  const fail = useCallback(
    (err: unknown) => toast({ tone: "error", title: err instanceof ApiError ? err.message : "That didn't work. Try again." }),
    [toast],
  );

  const flags = useMutation({
    mutationFn: async ({ rows, add = [], remove = [] }: { rows: Row[]; add?: string[]; remove?: string[] }) => {
      for (const g of byMailboxFolder(rows, mb)) await wmApi.post(`/m/${g.mailbox}/messages/flags`, { folder: g.folder, uids: g.uids, add, remove });
    },
    onSettled: refresh,
    onError: fail,
  });

  const move = useMutation({
    /** `say`: the sentence for the toast once the server has done it, e.g. "Archived 2 conversations." */
    mutationFn: async ({ rows, to }: { rows: Row[]; to: string; say?: string }) => {
      const undo: { mailbox: string; from: string; to: string; uids: number[] }[] = [];
      for (const g of byMailboxFolder(rows, mb)) {
        const r = await wmApi.post<{ moved: number; to: string; uids: number[] }>(`/m/${g.mailbox}/messages/move`, { folder: g.folder, uids: g.uids, to });
        if (r.uids.length) undo.push({ mailbox: g.mailbox, from: r.to, to: g.folder, uids: r.uids });
      }
      return undo;
    },
    onSuccess: (undo, { say }) => {
      if (!say) return;
      toast({
        tone: "success",
        title: say,
        action: undo.length
          ? {
              label: "Undo",
              onClick: async () => {
                try {
                  for (const u of undo) await wmApi.post(`/m/${u.mailbox}/messages/move`, { folder: u.from, uids: u.uids, to: u.to });
                  toast({ tone: "info", title: "Moved back." });
                } catch (err) {
                  fail(err);
                } finally {
                  void refresh();
                }
              },
            }
          : undefined,
      });
    },
    onSettled: refresh,
    onError: fail,
  });

  const destroy = useMutation({
    mutationFn: async ({ rows }: { rows: Row[] }) => {
      let n = 0;
      for (const g of byMailboxFolder(rows, mb)) n += (await wmApi.post<{ deleted: number }>(`/m/${g.mailbox}/messages/delete`, { folder: g.folder, uids: g.uids })).deleted;
      return n;
    },
    onSuccess: (n) => toast({ tone: "success", title: `Deleted ${describe(n, "message")} for good.` }),
    onSettled: refresh,
    onError: fail,
  });

  const empty = useMutation({
    mutationFn: async ({ path }: { path: string; name: string }) => wmApi.post<{ deleted: number }>(`/m/${mb}/folders/empty`, { path }),
    onSuccess: (r, { name }) => toast({ tone: "success", title: r.deleted ? `Emptied ${name}: ${describe(r.deleted, "message")} deleted for good.` : `${name} was already empty.` }),
    onSettled: refresh,
    onError: fail,
  });

  /** Snooze until a time: the conversations go to Snoozed and come back to Inbox, unread, then. Undo brings them back now. */
  const snooze = useMutation({
    mutationFn: async ({ rows, until }: { rows: Row[]; until: string }) => {
      const back: { mailbox: string; ids: string[] }[] = [];
      for (const g of byMailboxFolder(rows, mb)) {
        const r = await wmApi.post<{ snoozed: SnoozedMessage[] }>(`/m/${g.mailbox}/messages/snooze`, { folder: g.folder, uids: g.uids, until });
        back.push({ mailbox: g.mailbox, ids: r.snoozed.map((x) => x.id) });
      }
      return back;
    },
    onSuccess: (back, { rows, until }) =>
      toast({
        tone: "success",
        title: `Snoozed ${describe(rows.length)} until ${whenWords(until)}.`,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              for (const b of back) if (b.ids.length) await wmApi.post(`/m/${b.mailbox}/messages/unsnooze`, { ids: b.ids });
              toast({ tone: "info", title: "Back in Inbox." });
            } catch (err) {
              fail(err);
            } finally {
              void refresh();
            }
          },
        },
      }),
    onSettled: refresh,
    onError: fail,
  });

  /** Labels on and off (IMAP keywords; the names are the mailbox's, in prefs). */
  const label = useMutation({
    mutationFn: async ({ rows, add = [], remove = [] }: { rows: Row[]; add?: string[]; remove?: string[] }) => {
      for (const g of byMailboxFolder(rows, mb)) await wmApi.post(`/m/${g.mailbox}/messages/labels`, { folder: g.folder, uids: g.uids, add, remove });
    },
    onSettled: refresh,
    onError: fail,
  });

  /** Mute: new replies in the conversation skip Inbox (to Archive, already read). Undo unmutes. */
  const mute = useMutation({
    mutationFn: async ({ rows, muted }: { rows: Row[]; muted: boolean }) => {
      for (const g of byMailboxFolder(rows, mb)) await wmApi.post(`/m/${g.mailbox}/messages/mute`, { folder: g.folder, uids: g.uids, muted });
    },
    onSuccess: (_r, { rows, muted }) =>
      toast(
        muted
          ? {
              tone: "success",
              title: "Muted. New replies go to Archive, already read.",
              action: {
                label: "Undo",
                onClick: async () => {
                  try {
                    for (const g of byMailboxFolder(rows, mb)) await wmApi.post(`/m/${g.mailbox}/messages/mute`, { folder: g.folder, uids: g.uids, muted: false });
                    toast({ tone: "info", title: "Unmuted." });
                  } catch (err) {
                    fail(err);
                  } finally {
                    void refresh();
                  }
                },
              },
            }
          : { tone: "success", title: "Unmuted. New replies come to Inbox again." },
      ),
    onSettled: refresh,
    // A 409 here means the mail server keeps nothing on messages, which the API says in plain words.
    onError: fail,
  });

  return { flags, move, destroy, empty, snooze, label, mute, refresh };
}
